import { useAppTheme } from "../../../hooks/useAppTheme";
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  Modal as RNModal,
  View as RNView,
  Pressable,
  Platform,
} from "react-native";
import { DataTable } from "react-native-paper";
import { View } from "../../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import Spinner from "../../../components/shared/Spinner";
import { Ionicons } from "@expo/vector-icons";
import { getPmxDetailsInventory } from "../../../services/inventoryService";
import { getS3SignUrl } from "../../../services/s3BucketService";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  headersPmxReport,
  mdHeadersPmxReport,
  smHeadersPmxReport,
} from "../utils/pmxInventoryHeaders";
import { validSignedURLWithExt } from "../../../utils/validateSignedUrl";
import { pmxDetailModel } from "../../../models/reportsModel";
import { useSelector } from "react-redux";
import { handleTableLoader, renameLabel } from "../../../utils/common";
import { userClaimInformation } from "../../../models/usersModel";
import { sortColumns } from "../../../utils/sortColumns";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import { useIsFocused } from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { pmxInventoryTranslationMap } from "../../../utils/translations";
// @ts-ignore
import { Feather } from "@expo/vector-icons";

export default function PmxDetailsInventoryReportScreen({
  route,
  navigation,
}: any) {
  const { colors, isDark } = useAppTheme();
  const isFocused = useIsFocused();
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);
  const { t } = useTranslation();
  const styles = React.useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const cardStyles = React.useMemo(() => createCardStyles(colors, isDark), [colors, isDark]);
  const filterStyles = React.useMemo(() => getFilterStyles(colors), [colors]);
  const [mobilePage, setMobilePage] = useState<number>(0);
  const itemsPerPage = 20;
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const {
    pmxInventoryDetailReport,
    pmxInventoryDetailFilterCategoryList,
    isPmxInventoryDetailReportListLoaded,
  } = useSelector((state: any) => state.reports);
  const { userInfo, language } = useSelector(
    (state: any) => state.configurations,
  );

  const [selected, setSelected] = useState<pmxDetailModel>(
    {} as unknown as pmxDetailModel,
  );

  const [pmxDetailPageLevelList, setPmxDetailPageLevelList] = useState<
    Array<pmxDetailModel>
  >([]);
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headersPmxReport,
    );
  const [searchedText, setSearchedText] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("pmx_details_inventory_report_hidden_columns").then(
      (stored) => {
        if (stored) {
          try {
            setHiddenColumns(JSON.parse(stored));
          } catch { }
        }
      },
    );
  }, []);

  useEffect(() => {
    navigation.setOptions({ title: t("inventoryDetail") });
  }, [navigation, language, t]);

  // All available headers based on device size
  const getAllHeaders = () => {
    const base = isLargeDevice
      ? headersPmxReport
      : isMediumDevice
        ? mdHeadersPmxReport
        : smHeadersPmxReport;
    return translateHeaders(base, t, pmxInventoryTranslationMap);
  };

  const getOrderedKeys = (
    baseHeaders: Record<string, Record<string, string | number | boolean>>,
  ) => {
    const allKeys = Object.keys(baseHeaders);
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k) => {
      if (allKeys.includes(k)) ordered.push(k);
    });
    allKeys.forEach((k) => {
      if (!ordered.includes(k)) ordered.push(k);
    });
    return ordered;
  };

  const getFilteredHeaders = (
    baseHeaders: Record<string, Record<string, string | number | boolean>>,
  ) => {
    const orderedKeys = getOrderedKeys(baseHeaders);
    const visibleEntries = orderedKeys
      .filter((key) => !hiddenColumns.includes(key))
      .map(
        (key) =>
          [key, baseHeaders[key]] as [
            string,
            Record<string, string | number | boolean>,
          ],
      );
    const totalOriginalFlex = Object.values(baseHeaders).reduce(
      (sum, h) => sum + (Number(h.width) || 0),
      0,
    );
    const totalVisibleFlex = visibleEntries.reduce(
      (sum, [, h]) => sum + (Number(h.width) || 0),
      0,
    );
    const scale =
      totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<
      string,
      Record<string, string | number | boolean>
    > = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  // Update table headers when hidden columns change or device size changes
  useEffect(() => {
    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
  }, [hiddenColumns, isLargeDevice, isMobileDevice, isMediumDevice, language]);

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem(
        "pmx_details_inventory_report_hidden_columns",
        JSON.stringify(updated),
      );
      return updated;
    });
  };

  const moveColumn = useCallback(
    (fromKey: string, toKey: string) => {
      const allHeaders = getAllHeaders();
      const currentKeys = getOrderedKeys(allHeaders).filter((k) => k !== "");
      const fromIdx = currentKeys.indexOf(fromKey);
      const toIdx = currentKeys.indexOf(toKey);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      const newKeys = [...currentKeys];
      newKeys.splice(fromIdx, 1);
      newKeys.splice(toIdx, 0, fromKey);
      newKeys.push("");
      setColumnOrder(newKeys);
      AsyncStorage.setItem(
        "pmx_details_inventory_column_order",
        JSON.stringify(newKeys),
      );
    },
    [columnOrder],
  );

  const resetColumnOrder = () => {
    setColumnOrder([]);
    AsyncStorage.removeItem("pmx_details_inventory_column_order");
  };

  // Drag-and-drop reordering via window mouse events
  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({
    activeKey: null,
    overKey: null,
  });

  const startColumnDrag = useCallback(
    (key: string) => {
      if (Platform.OS !== "web" || typeof window === "undefined") return;
      dragRef.current.activeKey = key;
      setDragActiveKey(key);

      const onMouseMove = (e: MouseEvent) => {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const row =
          el && (el as HTMLElement).closest
            ? (el as HTMLElement).closest("[data-columnkey]")
            : null;
        const overKey = row ? row.getAttribute("data-columnkey") : null;
        if (overKey !== dragRef.current.overKey) {
          dragRef.current.overKey = overKey;
          setDragOverKey(
            overKey && overKey !== dragRef.current.activeKey ? overKey : null,
          );
        }
      };

      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        (document.body.style as any).userSelect = "";
        const from = dragRef.current.activeKey;
        const to = dragRef.current.overKey;
        if (from && to && from !== to) {
          moveColumn(from, to);
        }
        dragRef.current = { activeKey: null, overKey: null };
        setDragActiveKey(null);
        setDragOverKey(null);
      };

      document.body.style.cursor = "grabbing";
      (document.body.style as any).userSelect = "none";
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [moveColumn],
  );

  const columnsToNotHaveThousandsSeparator: Array<string> = [
    "Batch number",
    "SSCC number",
    "Uom1",
    "Uom2",
  ];

  const [_filterCategory, _setFilterCategory] = useState<string>("all");

  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedPmxDetailList: Array<pmxDetailModel> = sortColumns(
      pmxDetailPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders,
    );

    setPmxDetailPageLevelList(_sortedPmxDetailList);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();

    if (searchText.trim() !== "") {
      setSearchedText(searchText);

      const filteredListBasedOnSearchOrCondition: Array<pmxDetailModel> =
        filterBasedOnAnyKey(pmxInventoryDetailReport, searchText);

      const _filterPmxByCategory = filterPmxByCategory(
        filteredListBasedOnSearchOrCondition,
        _filterCategory,
      );
      setPmxDetailPageLevelList(recordTransformation(_filterPmxByCategory));
    } else {
      setSearchedText("");
      const _filterPmxByCategory = filterPmxByCategory(
        pmxInventoryDetailReport,
        _filterCategory,
      );
      setPmxDetailPageLevelList(recordTransformation(_filterPmxByCategory));
    }

    setTimeout(() => {
      fadeTableLoader();
    }, 1000);
  };

  const recordTransformation = (data: Array<pmxDetailModel>) => {
    const pmxData: Array<pmxDetailModel> = [];
    data.forEach((item: pmxDetailModel) => {
      pmxData?.push({
        ...item,
        BatchNumber1: item.BatchNumber1 === null ? "N/A" : item.BatchNumber1,
        SsccNumber: item.SsccNumber === null ? "N/A" : item.SsccNumber,
        QuantityUom:
          item.QuantityUom === null
            ? "0"
            : `${(item.QuantityUom as unknown as number)?.toLocaleString()}`,
        QuantityUom2:
          item.QuantityUom2 === null
            ? "0"
            : `${(item.QuantityUom2 as unknown as number)?.toLocaleString()}`,
      });
    });

    return pmxData;
  };

  const filterPmxByCategory = (
    _pmxsReport: Array<pmxDetailModel>,
    _filterCategory: string,
  ) => {
    return _pmxsReport.filter((pmx: pmxDetailModel) => {
      switch (_filterCategory) {
        case "NSW":
          return pmx.Warehouse === "01";
          break;
        case "VIC":
          return pmx.Warehouse === "04";
          break;
        case "QLD":
          return ["02", "14"].includes(pmx.Warehouse);
          break;
        case "TAS":
          return ["06", "16", "12"].includes(pmx.Warehouse);
          break;
        case "WA":
          return pmx.Warehouse === "05";
          break;
        default:
          return pmx;
          break;
      }
    });
  };

  useEffect(() => {
    let isMounted: boolean = true;
    const _userInfo = userInfo as userClaimInformation;

    // Avoid keep refreshing table when item is selected
    if (isMounted && modalVisible === false) {
      if (!isPmxInventoryDetailReportListLoaded || isRefresh) {
        displayTableLoader();

        _setFilterCategory("all");
        setSearchedText("");

        getPmxDetailsInventory(
          "",
          _userInfo.team,
          _userInfo.zone_info || "",
          tableHeaders,
        );
      }
    }

    if (selected?.PicturName) {
      validSignedURLWithExt(
        selected.PicturName || "",
        "label",
        setImageLoading,
        setSelectedImage,
      );
    } else if (Object.keys(selected).length !== 0) {
      getS3SignUrl("multisteps-label", "no-image.png", "getObject", "image/*").then(
        (data) => {
          setSelectedImage(data?.signedUrl as string);
          setImageLoading(false);
        },
      );
    }

    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isFocused, selected, isRefresh]);

  useEffect(() => {
    setImageLoading(true);
  }, [modalVisible]);

  useEffect(() => {
    setPmxDetailPageLevelList(recordTransformation(pmxInventoryDetailReport));

    setTimeout(() => {
      fadeTableLoader();
    }, 2000);
  }, [pmxInventoryDetailReport]);

  useEffect(() => {
    displayTableLoader();

    const _pmxsReport = recordTransformation(
      pmxInventoryDetailReport,
    ) as Array<pmxDetailModel>;
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_pmxsReport, searchedText)
        : _pmxsReport;

    setPmxDetailPageLevelList(
      filterPmxByCategory(
        filteredListBasedOnSearchOrCondition,
        _filterCategory,
      ),
    );

    fadeTableLoader();
  }, [_filterCategory]);

  const navigateToOrder = (order: any, action: string) => {
    navigation.navigate("PmxDetailsInventoryDetailReportScreen", { order });
  };

  const MobilePmxDetailCard = ({ pmx }: { pmx: pmxDetailModel }) => {
    return (
      <TouchableOpacity
        style={cardStyles.card}
        onPress={() => navigateToOrder(pmx, "view")}
        activeOpacity={0.7}
      >
        <RNView style={cardStyles.cardHeader}>
          <RNView style={cardStyles.cardHeaderLeft}>
            <RNView style={cardStyles.containerIconWrap}>
              <Ionicons name="cube-outline" size={18} color={isDark ? colors.primary : "#3B82F6"} />
            </RNView>
            <RNView style={{ flex: 1, backgroundColor: "transparent" }}>
              <Text style={cardStyles.containerNumber}>{pmx.ItemName}</Text>
            </RNView>
          </RNView>
        </RNView>

        <View style={cardStyles.divider} />
        <View style={cardStyles.dateRow}>
          <View style={cardStyles.dateBlock}>
            <Text style={cardStyles.dateLabel}>{pmx.InvntryUom ? pmx.InvntryUom.toUpperCase() : "UOM1"}</Text>
            <Text style={cardStyles.dateValue}>{pmx.QuantityUom || "0"}</Text>
          </View>
          <View style={{ width: 1, height: 24, backgroundColor: isDark ? colors.border : "#E2E8F0" }} />
          <View style={cardStyles.dateBlock}>
            <Text style={cardStyles.dateLabel}>{pmx.Uom2 ? pmx.Uom2.toUpperCase() : "UOM2"}</Text>
            <Text style={cardStyles.dateValue}>{pmx.QuantityUom2 || "0"}</Text>
          </View>
        </View>

        <View style={cardStyles.divider} />
        <View style={cardStyles.cardFooter}>
          <View style={cardStyles.metaChip}>
            <Ionicons name="pricetag-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
            <Text style={cardStyles.metaChipText}>{pmx.ItemCode}</Text>
          </View>
          {pmx.BatchNumber1 && pmx.BatchNumber1 !== "N/A" && (
            <View style={cardStyles.metaChip}>
              <Ionicons name="barcode-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
              <Text style={cardStyles.metaChipText} numberOfLines={1} ellipsizeMode="tail">{pmx.BatchNumber1}</Text>
            </View>
          )}
          {!!pmx.StorLocName && (
            <View style={cardStyles.metaChip}>
              <Ionicons name="location-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
              <Text style={cardStyles.metaChipText} numberOfLines={1} ellipsizeMode="tail">{pmx.StorLocName}</Text>
            </View>
          )}
          {!!pmx.Warehouse && (
            <View style={cardStyles.metaChip}>
              <Ionicons name="business-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
              <Text style={cardStyles.metaChipText} numberOfLines={1} ellipsizeMode="tail">{pmx.Warehouse}</Text>
            </View>
          )}
          {!!pmx.QuantityStatus && (
            <View style={[cardStyles.metaChip, { borderColor: colors.warning }]}>
              <Ionicons name="alert-circle-outline" size={12} color={colors.warning} />
              <Text style={[cardStyles.metaChipText, { color: colors.warning }]} numberOfLines={1} ellipsizeMode="tail">
                {pmx.QuantityStatus}
              </Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={{ flex: 1, backgroundColor: isDark ? colors.background : "#eef2f6" }}>
      <ScrollView
        style={{ flex: 1, backgroundColor: isDark ? colors.background : "#eef2f6" }}
        contentContainerStyle={{ flexGrow: 1, backgroundColor: isDark ? colors.background : "#eef2f6" }}
      >
        <View
          style={[
            styles.pageWrapper,
            { backgroundColor: columnSettingsOpen ? colors.primaryBg : (isDark ? colors.background : "#eef2f6") },
          ]}
        >
          {isPmxInventoryDetailReportListLoaded ? (
            <>
              <View style={[styles.tableCard, !(isSmallDevice || isMobileDevice) && { marginTop: 12 }, (isSmallDevice || isMobileDevice) && { marginBottom: 8 }]}>
                <LabelBannerWithSearchBar
                  titleName={`${t("totalItems")}: ${pmxDetailPageLevelList.length}`}
                  handleSearch={handleSearch}
                  setRefresh={setRefresh}
                  middleSearchControls={
                    userInfo.team === "operation" && (isSmallDevice || isMobileDevice) ? (
                      <TouchableOpacity
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          backgroundColor: showMobileFilters ? colors.primary : colors.surfaceSubtle,
                          justifyContent: "center",
                          alignItems: "center",
                          borderWidth: 1,
                          borderColor: showMobileFilters ? colors.primary : colors.border,
                        }}
                        onPress={() => setShowMobileFilters(!showMobileFilters)}
                      >
                        <Feather
                          name="filter"
                          size={14}
                          color={showMobileFilters ? "#FFFFFF" : "#6B7280"}
                        />
                      </TouchableOpacity>
                    ) : undefined
                  }
                  dataToExport={pmxDetailPageLevelList}
                  fileName={"inventory_details_report"}
                  preSearchControls={
                    !(isSmallDevice || isMobileDevice) ? (
                      <RNView>
                        <TouchableOpacity
                          ref={settingsBtnRef}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            backgroundColor: colors.surfaceSubtle,
                            justifyContent: "center",
                            alignItems: "center",
                            borderWidth: 1,
                            borderColor: columnSettingsOpen ? colors.primary : colors.border,
                          }}
                          onPress={() => {
                            if (settingsBtnRef.current) {
                              settingsBtnRef.current.measureInWindow(
                                (
                                  x: number,
                                  y: number,
                                  width: number,
                                  height: number,
                                ) => {
                                  setSettingsBtnPos({
                                    x: x || 0,
                                    y: y || 0,
                                    width: width || 0,
                                    height: height || 0,
                                  });
                                  setColumnSettingsOpen(!columnSettingsOpen);
                                },
                              );
                            } else {
                              setColumnSettingsOpen(!columnSettingsOpen);
                            }
                          }}
                        >
                          <Feather
                            name="settings"
                            size={15}
                            color={columnSettingsOpen ? colors.primary : colors.textSecondary}
                          />
                        </TouchableOpacity>
                        {columnSettingsOpen && (
                          <RNModal
                            transparent={true}
                            visible={columnSettingsOpen}
                            animationType="fade"
                            onRequestClose={() => setColumnSettingsOpen(false)}
                          >
                            <Pressable
                              style={{
                                flex: 1,
                                backgroundColor: "rgba(0,0,0,0.15)",
                              }}
                              onPress={() => setColumnSettingsOpen(false)}
                            >
                              <Pressable
                                style={{
                                  position: "absolute",
                                  top: settingsBtnPos.y + settingsBtnPos.height + 6,
                                  left: settingsBtnPos.x - 180 + 40,
                                  backgroundColor: colors.surface,
                                  borderRadius: 12,
                                  borderWidth: 1,
                                  borderColor: colors.border,
                                  paddingVertical: 10,
                                  minWidth: 220,
                                  shadowColor: "#000",
                                  shadowOffset: { width: 0, height: 8 },
                                  shadowOpacity: 0.15,
                                  shadowRadius: 20,
                                  elevation: 10,
                                }}
                                onPress={(e) => e.stopPropagation()}
                              >
                                <Text
                                  style={{
                                    fontSize: 12,
                                    fontWeight: "700",
                                    color: colors.textSecondary,
                                    paddingHorizontal: 16,
                                    paddingBottom: 8,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  {t("showHideColumns")}
                                </Text>
                                <View
                                  style={{
                                    height: 1,
                                    backgroundColor: colors.borderSubtle,
                                    marginBottom: 4,
                                  }}
                                />
                                {/* Select All */}
                                <TouchableOpacity
                                  style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    paddingVertical: 8,
                                    paddingHorizontal: 16,
                                  }}
                                  onPress={() => {
                                    const allKeys = Object.keys(getAllHeaders()).filter(
                                      (k) => k !== "",
                                    );
                                    if (hiddenColumns.length === 0) {
                                      const updated = [...allKeys];
                                      setHiddenColumns(updated);
                                      AsyncStorage.setItem(
                                        "pmx_details_inventory_report_hidden_columns",
                                        JSON.stringify(updated),
                                      );
                                    } else {
                                      setHiddenColumns([]);
                                      AsyncStorage.setItem(
                                        "pmx_details_inventory_report_hidden_columns",
                                        JSON.stringify([]),
                                      );
                                    }
                                  }}
                                >
                                  <View
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 4,
                                      borderWidth: 1.5,
                                      borderColor:
                                        hiddenColumns.length === 0
                                          ? colors.primary
                                          : colors.textMuted,
                                      backgroundColor:
                                        hiddenColumns.length === 0 ? colors.primary : colors.surface,
                                      justifyContent: "center",
                                      alignItems: "center",
                                      marginRight: 10,
                                    }}
                                  >
                                    {hiddenColumns.length === 0 && (
                                      <Feather name="check" size={13} color="#fff" />
                                    )}
                                  </View>
                                  <Text
                                    style={{
                                      fontSize: 14,
                                      color: colors.text,
                                      fontWeight: "700",
                                    }}
                                  >
                                    {t("selectAll")}
                                  </Text>
                                </TouchableOpacity>
                                <View
                                  style={{
                                    height: 1,
                                    backgroundColor: colors.borderSubtle,
                                    marginBottom: 2,
                                  }}
                                />
                                <ScrollView
                                  style={{ maxHeight: 300 }}
                                  showsVerticalScrollIndicator={true}
                                >
                                  {getOrderedKeys(getAllHeaders()).map((key) => {
                                    if (key === "") return null;
                                    const value = getAllHeaders()[key];
                                    if (!value) return null;
                                    const isVisible = !hiddenColumns.includes(key);
                                    const isDragging = dragActiveKey === key;
                                    const isDragOver = dragOverKey === key;
                                    return (
                                      <View
                                        key={key}
                                        {...({ dataSet: { columnkey: key } } as any)}
                                        style={{
                                          flexDirection: "row",
                                          alignItems: "center",
                                          paddingVertical: 8,
                                          paddingHorizontal: 16,
                                          opacity: isDragging ? 0.35 : 1,
                                          backgroundColor: isDragOver
                                            ? colors.primaryBg
                                            : "transparent",
                                          borderTopWidth: isDragOver ? 2 : 0,
                                          borderTopColor: colors.primary,
                                        }}
                                      >
                                        <Pressable
                                          onPressIn={() => startColumnDrag(key)}
                                          style={
                                            {
                                              padding: 4,
                                              marginRight: 6,
                                              cursor: "grab",
                                            } as any
                                          }
                                        >
                                          <Feather
                                            name="menu"
                                            size={14}
                                            color={colors.textMuted}
                                          />
                                        </Pressable>
                                        <TouchableOpacity
                                          style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            flex: 1,
                                          }}
                                          onPress={() => toggleColumn(key)}
                                        >
                                          <View
                                            style={{
                                              width: 20,
                                              height: 20,
                                              borderRadius: 4,
                                              borderWidth: 1.5,
                                              borderColor: isVisible
                                                ? colors.primary
                                                : colors.textMuted,
                                              backgroundColor: isVisible
                                                ? colors.primary : colors.surface,
                                              justifyContent: "center",
                                              alignItems: "center",
                                              marginRight: 10,
                                            }}
                                          >
                                            {isVisible && (
                                              <Feather
                                                name="check"
                                                size={13}
                                                color="#fff"
                                              />
                                            )}
                                          </View>
                                          <Text
                                            style={{
                                              fontSize: 14,
                                              color: isVisible ? colors.text : colors.textMuted,
                                              fontWeight: isVisible ? "600" : "400",
                                            }}
                                          >
                                            {(value as any).name}
                                          </Text>
                                        </TouchableOpacity>
                                      </View>
                                    );
                                  })}
                                </ScrollView>
                                {columnOrder.length > 0 && (
                                  <>
                                    <View
                                      style={{
                                        height: 1,
                                        backgroundColor: colors.borderSubtle,
                                        marginTop: 4,
                                      }}
                                    />
                                    <TouchableOpacity
                                      style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        paddingVertical: 10,
                                        paddingHorizontal: 16,
                                      }}
                                      onPress={resetColumnOrder}
                                    >
                                      <Feather
                                        name="rotate-ccw"
                                        size={13}
                                        color={colors.textSecondary}
                                      />
                                      <Text
                                        style={{
                                          fontSize: 13,
                                          color: colors.textSecondary,
                                          fontWeight: "600",
                                          marginLeft: 6,
                                        }}
                                      >
                                        {t("resetOrder")}
                                      </Text>
                                    </TouchableOpacity>
                                  </>
                                )}
                              </Pressable>
                            </Pressable>
                          </RNModal>
                        )}

                      </RNView>
                    ) : undefined
                  }
                />
                {userInfo.team === "operation" && ((isSmallDevice || isMobileDevice) ? showMobileFilters : true) && (
                  <View style={filterStyles.renderFilterStyles}>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      style={{ width: "100%" }}
                      contentContainerStyle={filterStyles.filterContainer}
                    >
                      {!(isSmallDevice || isMobileDevice) && (
                        <Text style={filterStyles.quickFilterText}>{t("quickFilter")}</Text>
                      )}
                      {pmxInventoryDetailFilterCategoryList.map(
                        (category: string, index: number) => {
                          const isActive = _filterCategory === category;
                          return (
                            <TouchableOpacity
                              key={index}
                              style={[
                                filterStyles.filterButton,
                                isActive && filterStyles.activeFilterButton,
                              ]}
                              onPress={() => _setFilterCategory(category)}
                            >
                              <Text
                                style={
                                  isActive
                                    ? filterStyles.activeFilterText
                                    : filterStyles.filterText
                                }
                              >
                                {renameLabel(category)}
                              </Text>
                            </TouchableOpacity>
                          );
                        },
                      )}
                    </ScrollView>
                  </View>
                )}
                {!(isSmallDevice || isMobileDevice) && (
                  <DataTableWithPagination
                    headers={tableHeaders}
                    rows={pmxDetailPageLevelList}
                    itemsPerPage={100}
                    useModal={true}
                    isRowsExpandable={false}
                    setModalVisible={setModalVisible}
                    setSelected={setSelected}
                    isDetail={true}
                    isDataLoading={isSearched}
                    columnsToNotHaveThousandSeparator={
                      columnsToNotHaveThousandsSeparator
                    }
                    handleRowClick={() => { }}
                    handleDeleteRow={() => { }}
                    handleSortColumn={onHandleSortColumn}
                    operationActions={navigateToOrder}
                    bottomCornerRadius={16}
                  />
                )}
              </View>

              {(isSmallDevice || isMobileDevice) && (
                <View style={cardStyles.cardList}>
                  {pmxDetailPageLevelList.length === 0 ? (
                    <View style={cardStyles.emptyState}>
                      <Ionicons name="cube-outline" size={48} color="#CBD5E1" />
                      <Text style={cardStyles.emptyStateText}>{t("noDataAvailable")}</Text>
                    </View>
                  ) : (
                    <>
                      {pmxDetailPageLevelList
                        .slice(mobilePage * itemsPerPage, (mobilePage + 1) * itemsPerPage)
                        .map((pmx, index) => (
                          <MobilePmxDetailCard key={`${pmx.ItemCode || "item"}-${index}`} pmx={pmx} />
                        ))}
                      <DataTable.Pagination
                        page={mobilePage}
                        numberOfPages={Math.ceil(pmxDetailPageLevelList.length / itemsPerPage)}
                        onPageChange={(page) => setMobilePage(page)}
                        label={`${mobilePage * itemsPerPage + 1}-${Math.min(
                          (mobilePage + 1) * itemsPerPage,
                          pmxDetailPageLevelList.length,
                        )} of ${pmxDetailPageLevelList.length}`}
                        showFastPaginationControls
                      />
                    </>
                  )}
                </View>
              )}
            </>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const createStyles = (colors: any, isDark: boolean) => StyleSheet.create({
  tableCard: {
    marginHorizontal: 16,
    marginBottom: 24,
    marginTop: 12,
    backgroundColor: isDark ? colors.surface : "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: isDark ? colors.border : "#E2E8F0",
    shadowColor: isDark ? colors.text : "#0F172A",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    elevation: 4,
  },
  pageWrapper: {
    flex: 1,
    minHeight: "100%" as any,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
});

function getFilterStyles(colors: any) {
  return StyleSheet.create({
    renderFilterStyles: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      width: "100%",
      paddingTop: 4,
      paddingBottom: 16,
      marginTop: 0,
    },
    filterContainer: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 2,
      paddingHorizontal: 16,
    },
    quickFilterText: {
      display: "flex",
      alignItems: "center",
      marginRight: 10,
      color: colors.text,
    },
    filterButton: {
      paddingVertical: 7,
      paddingHorizontal: 14,
      backgroundColor: colors.surfaceSubtle,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 999,
      marginRight: 8,
    },
    activeFilterButton: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    filterText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontWeight: "500",
    },
    activeFilterText: {
      color: "#fff",
      fontSize: 13,
      fontWeight: "600",
    },
  });
}

function createCardStyles(colors: any, isDark: boolean) {
  return StyleSheet.create({
    cardList: {
      paddingHorizontal: 16,
      paddingBottom: 24,
      paddingTop: 8,
      gap: 12,
      backgroundColor: "transparent",
    },
    card: {
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E8EDF5",
      shadowColor: isDark ? colors.text : "#1E293B",
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.07,
      shadowRadius: 10,
      elevation: 3,
      overflow: "hidden" as any,
      marginBottom: 4,
    },
    cardHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
      paddingVertical: 14,
      backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
    },
    cardHeaderLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      backgroundColor: "transparent",
      flex: 1,
      paddingRight: 10,
    },
    containerIconWrap: {
      width: 34,
      height: 34,
      borderRadius: 10,
      backgroundColor: isDark ? colors.primary + "33" : "#E1F0FA",
      alignItems: "center",
      justifyContent: "center",
    },
    containerNumber: {
      fontSize: 14,
      fontWeight: "700" as const,
      color: isDark ? colors.text : "#0F172A",
      letterSpacing: 0.2,
      flexWrap: "wrap",
      backgroundColor: "transparent",
    },
    divider: {
      height: 1,
      backgroundColor: isDark ? colors.background : "#eef2f6",
      marginHorizontal: 16,
    },
    dateRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
      paddingVertical: 14,
      gap: 4,
    },
    dateBlock: {
      flex: 1,
      alignItems: "center",
      gap: 3,
    },
    dateLabel: {
      fontSize: 10,
      color: isDark ? colors.textSecondary : "#94A3B8",
      fontWeight: "600" as const,
      textTransform: "uppercase" as any,
      letterSpacing: 0.5,
    },
    dateValue: {
      fontSize: 13,
      color: isDark ? colors.text : "#1E293B",
      fontWeight: "700" as const,
    },
    cardFooter: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap" as any,
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderTopWidth: 1,
      borderTopColor: isDark ? colors.background : "#eef2f6",
      backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
      gap: 8,
    },
    metaChip: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 6,
      paddingVertical: 4,
      paddingHorizontal: 8,
      gap: 4,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      flexShrink: 1,
      maxWidth: "100%",
    },
    metaChipText: {
      fontSize: 11,
      color: isDark ? colors.textSecondary : "#6B7280",
      fontWeight: "500" as const,
      flexShrink: 1,
    },
    emptyState: {
      alignItems: "center",
      paddingVertical: 60,
      gap: 12,
    },
    emptyStateText: {
      fontSize: 15,
      color: "#CBD5E1",
      fontWeight: "500" as const,
    },
  });
}
